/**
 * ProfileSection — display name (debounced auto-save), avatar upload,
 * PM token balance with a "Buy more PM" shortcut.
 *
 * Auto-save pattern mirrors skillful's LearnerAccountPage:150-223 — 1000ms
 * debounce, inline "Saving…" / "Saved" / "Unsaved changes" status line,
 * `flushPendingSaves` imperative handle so the parent modal can drain
 * pending writes before it closes.
 */

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Coins, User } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { updateUserProfile } from '@/lib/auth-service';
import { useAuthStore } from '@/lib/stores/authStore';
import { nextPmBalanceFetchSeq, usePmBalanceStore } from '@/lib/stores/pmBalanceStore';
import { DEFAULT_PLAYER_AVATAR } from '@/lib/media';
import { toast } from '@/lib/toast';
import { FirebaseStorageUpload } from '@/components/common/FirebaseStorageUpload';
import { RewardsBlock } from '@/components/account/sections/RewardsBlock';
import { useNavigate } from 'react-router-dom';

export interface ProfileSectionHandle {
  /** Cancel the debounce timer, fire pending save, await it. */
  flushPendingSaves: () => Promise<void>;
}

const DISPLAY_NAME_DEBOUNCE_MS = 1000;
const NAME_MAX_LENGTH = 50;

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const ProfileSection = forwardRef<ProfileSectionHandle>((_props, ref) => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(
    user?.displayName || user?.email?.split('@')[0] || ''
  );
  const [initialName, setInitialName] = useState(
    user?.displayName || user?.email?.split('@')[0] || ''
  );
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [pmBalance, setPmBalance] = useState<number | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveRef = useRef<Promise<void> | null>(null);
  const savedIndicatorRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch PM balance on mount. Write through to the shared store so the
  // send-time gates (`ChatPanel`, `submit-tap-intent`, `websocket-commands`)
  // see the same value — mirrors `UserMenu`.
  useEffect(() => {
    let cancelled = false;
    // Sequence-guarded (night-watch 2026-07-11, LOW): a delayed mount fetch
    // could otherwise resolve AFTER `refreshBalance`'s post-reward fetch and
    // overwrite the fresher balance with the stale pre-credit one.
    const seq = nextPmBalanceFetchSeq();
    apiClient
      .getPmBalance()
      .then(({ pmTokenBalance }) => {
        if (cancelled) return;
        setPmBalance(pmTokenBalance);
        usePmBalanceStore.getState().setPmBalance(pmTokenBalance, seq);
      })
      .catch(() => {
        // Leave the store value as-is on failure — last WS push is still
        // better than wiping the gate to "unknown" mid-session.
        if (!cancelled) setPmBalance(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-pull the balance after a reward credits it (skip the short-TTL cache)
  // and write through to the shared store so the low-balance gate unlocks
  // immediately after the credit, not on the next remount/reconnect.
  const refreshBalance = useCallback(() => {
    const seq = nextPmBalanceFetchSeq();
    apiClient
      .getPmBalance({ skipCache: true })
      .then(({ pmTokenBalance }) => {
        setPmBalance(pmTokenBalance);
        usePmBalanceStore.getState().setPmBalance(pmTokenBalance, seq);
      })
      .catch(() => {
        // Leave the store value as-is on failure.
      });
  }, []);

  // Keep local input in sync if the upstream store updates (e.g. from
  // elsewhere in the app). Skip if we're the ones saving.
  useEffect(() => {
    if (saveStatus === 'saving') return;
    const upstream = user?.displayName || user?.email?.split('@')[0] || '';
    setInitialName(upstream);
    setDisplayName((prev) => (prev === initialName ? upstream : prev));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.displayName]);

  const performSave = useCallback(
    async (name: string): Promise<void> => {
      const trimmed = name.trim();
      if (!trimmed || trimmed === initialName.trim()) return;
      if (trimmed.length > NAME_MAX_LENGTH) return;

      setSaveStatus('saving');
      try {
        // Backend may adjust to a unique name (e.g. 'bob' → 'bob2'); reflect it,
        // but only if the user hasn't kept typing during the in-flight save —
        // otherwise we'd clobber newer keystrokes. initialName always advances so
        // any continued typing re-saves.
        const assigned = (await updateUserProfile({ displayName: trimmed })) ?? trimmed;
        setDisplayName((cur) => (cur.trim() === trimmed ? assigned : cur));
        setInitialName(assigned);
        setSaveStatus('saved');
        if (savedIndicatorRef.current) clearTimeout(savedIndicatorRef.current);
        savedIndicatorRef.current = setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('error');
        toast.error('Failed to save display name');
      }
    },
    [initialName]
  );

  // Debounced auto-save on change.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = displayName.trim();
    if (!trimmed || trimmed === initialName.trim() || trimmed.length > NAME_MAX_LENGTH) {
      return;
    }

    debounceRef.current = setTimeout(() => {
      const savePromise = performSave(displayName);
      pendingSaveRef.current = savePromise;
      savePromise.finally(() => {
        if (pendingSaveRef.current === savePromise) pendingSaveRef.current = null;
      });
    }, DISPLAY_NAME_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [displayName, initialName, performSave]);

  // Cleanup timers on unmount.
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedIndicatorRef.current) clearTimeout(savedIndicatorRef.current);
    };
  }, []);

  // Imperative flush — parent modal calls before close.
  useImperativeHandle(ref, () => ({
    flushPendingSaves: async () => {
      // Cancel any debounced-but-not-yet-fired save and run it now.
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        const trimmed = displayName.trim();
        if (trimmed && trimmed !== initialName.trim() && trimmed.length <= NAME_MAX_LENGTH) {
          const savePromise = performSave(displayName);
          pendingSaveRef.current = savePromise;
          await savePromise;
          pendingSaveRef.current = null;
          return;
        }
      }
      // Or wait for an already-running save to finish.
      if (pendingSaveRef.current) {
        await pendingSaveRef.current;
      }
    },
  }));

  const handleAvatarUpload = async (url: string) => {
    try {
      await updateUserProfile({ photoURL: url });
      toast.success('Avatar updated');
    } catch {
      toast.error('Failed to update avatar');
    }
  };

  if (!user) return null;

  const trimmed = displayName.trim();
  const isDirty = trimmed !== initialName.trim();
  const isValid = trimmed.length >= 1 && trimmed.length <= NAME_MAX_LENGTH;

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Profile</h1>
        <p className="text-sm text-muted-foreground">Update your display name and avatar.</p>
      </header>

      <div className="flex flex-col items-center gap-4">
        <div className="group relative">
          <Avatar className="h-28 w-28 border-2 border-border">
            <AvatarImage src={user.photoURL || DEFAULT_PLAYER_AVATAR} />
            <AvatarFallback className="text-2xl">
              {user.displayName?.[0]?.toUpperCase() || <User className="h-12 w-12" />}
            </AvatarFallback>
          </Avatar>
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
            <Camera className="h-6 w-6 text-white" />
          </div>
          <FirebaseStorageUpload
            storagePath={`avatars/${user.uid}/profile`}
            fileType="image"
            // Align with the Storage rule cap at `storage.rules` —
            // 1 MiB / `image/*`. The component default for `image` is
            // 5 MiB; without this override, 1–5 MiB selections pass
            // client-side validation and then fail server-side with
            // an opaque `storage/unauthorized`.
            maxFileSize={1 * 1024 * 1024}
            onUploadComplete={handleAvatarUpload}
            onUploadError={(err) => toast.error(`Upload failed: ${err.message}`)}
            hasExistingFile={!!user.photoURL}
            label=""
            hideIcon
            className="absolute inset-0 cursor-pointer opacity-0"
          />
        </div>
        <p className="text-xs text-muted-foreground">Click the avatar to upload a new image.</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="account-display-name">Display name</Label>
        <Input
          id="account-display-name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={NAME_MAX_LENGTH}
          placeholder="Enter your display name"
        />
        <div className="text-xs text-muted-foreground">
          Required, 1–{NAME_MAX_LENGTH} characters
          {saveStatus === 'saving' && <span className="ml-2">• Saving…</span>}
          {saveStatus === 'saved' && <span className="ml-2 text-green-600">• Saved</span>}
          {saveStatus === 'error' && <span className="ml-2 text-destructive">• Save failed</span>}
          {isDirty && isValid && saveStatus === 'idle' && (
            <span className="ml-2">• Unsaved changes</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Coins className="h-4 w-4 text-amber-500" />
          <span>PMT Balance</span>
        </div>
        <div className="flex items-center gap-3">
          <span
            className={`font-mono text-sm font-medium ${
              pmBalance !== null && pmBalance < 1 ? 'text-destructive' : 'text-foreground'
            }`}
          >
            {pmBalance !== null ? `${pmBalance.toFixed(2)} PMT` : '—'}
          </span>
          <Button size="sm" variant="outline" onClick={() => navigate('/buy-tokens')}>
            Buy more
          </Button>
        </div>
      </div>

      <RewardsBlock onCredited={refreshBalance} />
    </div>
  );
});

ProfileSection.displayName = 'ProfileSection';
